import { doc, setDoc, getDoc } from "firebase/firestore";
import { db } from "./firebase";
// User roles
export var UserRole;
(function (UserRole) {
    UserRole["ADMIN"] = "admin";
    UserRole["USER"] = "user";
})(UserRole || (UserRole = {}));
/**
 * Create a new user document in Firestore
 * @param uid User ID
 * @param email User email
 * @param displayName User display name
 * @param role User role (defaults to USER)
 */
export const createUserDocument = async (uid, email, displayName, role = UserRole.USER) => {
    try {
        const userRef = doc(db, "users", uid);
        const now = new Date().toISOString();
        const userData = {
            uid,
            email,
            role,
            displayName: displayName || email.split("@")[0],
            createdAt: now,
            updatedAt: now,
        };
        await setDoc(userRef, userData);
        console.log(`User document created for ${email} with role ${role}`);
    }
    catch (error) {
        console.error("Error creating user document:", error);
        throw error;
    }
};
/**
 * Get user data from Firestore
 * @param uid User ID
 * @returns User data or null if not found
 */
export const getUserData = async (uid) => {
    try {
        const userRef = doc(db, "users", uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
            return userSnap.data();
        }
        return null;
    }
    catch (error) {
        console.error("Error getting user data:", error);
        return null;
    }
};
/**
 * Update user role
 * @param uid User ID
 * @param role New role
 */
export const updateUserRole = async (uid, role) => {
    try {
        const userRef = doc(db, "users", uid);
        await setDoc(userRef, {
            role,
            updatedAt: new Date().toISOString(),
        }, { merge: true });
        console.log(`User role updated to ${role} for user ${uid}`);
    }
    catch (error) {
        console.error("Error updating user role:", error);
        throw error;
    }
};
/**
 * Check if a user is an admin
 * @param uid User ID
 * @returns True if user is admin, false otherwise
 */
export const isUserAdmin = async (uid) => {
    try {
        const userData = await getUserData(uid);
        return userData?.role === UserRole.ADMIN;
    }
    catch (error) {
        console.error("Error checking if user is admin:", error);
        return false;
    }
};
/**
 * Set up the initial admin user
 * This should be called once during application setup
 * @param email Admin email
 * @param uid Admin user ID
 */
export const setupInitialAdmin = async (email, uid) => {
    try {
        // Check if user document exists
        const userData = await getUserData(uid);
        if (!userData) {
            // Create new user document with admin role
            await createUserDocument(uid, email, undefined, UserRole.ADMIN);
            console.log(`Initial admin user created: ${email}`);
        }
        else if (userData.role !== UserRole.ADMIN) {
            // Update existing user to admin role
            await updateUserRole(uid, UserRole.ADMIN);
            console.log(`User ${email} updated to admin role`);
        }
        else {
            console.log(`User ${email} is already an admin`);
        }
    }
    catch (error) {
        console.error("Error setting up initial admin:", error);
        throw error;
    }
};
